import React from 'react';

/**
 * FIR-21161: where a release drops the underwriter. `ask` shows the chooser
 * every time; the rest are the remembered destinations it can set.
 */
export const AFTER_RELEASE = Object.freeze({
  ASK: 'ask',
  INBOX: 'inbox',
  FOLLOW_QUOTE: 'followQuote',
  ALL_QUOTES: 'allQuotes',
} as const);

export type AfterReleaseChoice =
  (typeof AFTER_RELEASE)[keyof typeof AFTER_RELEASE];

export interface UwPreferences {
  isStickyHeader: boolean;
  afterRelease: AfterReleaseChoice;
}

const DOMAIN = 'uwPreferences';

const DEFAULT_PREFERENCES: UwPreferences = {
  isStickyHeader: true,
  afterRelease: AFTER_RELEASE.ASK,
};

/**
 * Read straight from storage rather than through the hook. The release
 * handlers need the value as it is *now* — a hook's copy is captured at mount,
 * so a preference the chooser just wrote would be stale by the time the next
 * release reads it. Also usable from the legacy underwriter view, which has no
 * preferences menu of its own.
 */
export function readUwPreferences(): UwPreferences {
  return readFromStorage();
}

export function writeUwPreference<K extends keyof UwPreferences>(
  key: K,
  value: UwPreferences[K]
) {
  writeToStorage({ ...readFromStorage(), [key]: value });
}

function readFromStorage(): UwPreferences {
  try {
    const raw = localStorage.getItem('cowbell');
    const store = raw ? JSON.parse(raw) : {};
    return { ...DEFAULT_PREFERENCES, ...(store?.uiSettings?.[DOMAIN] ?? {}) };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writeToStorage(preferences: UwPreferences) {
  try {
    const raw = localStorage.getItem('cowbell');
    const store = raw ? JSON.parse(raw) : {};
    localStorage.setItem(
      'cowbell',
      JSON.stringify({
        ...store,
        uiSettings: {
          ...store.uiSettings,
          [DOMAIN]: preferences,
        },
      })
    );
  } catch {
    // silently fail
  }
}

export function useUwPreferences() {
  const [preferences, setPreferencesState] =
    React.useState<UwPreferences>(readFromStorage);

  const setPreference = React.useCallback(
    <K extends keyof UwPreferences>(key: K, value: UwPreferences[K]) => {
      setPreferencesState((current) => {
        const updated = { ...current, [key]: value };
        writeToStorage(updated);
        return updated;
      });
    },
    []
  );

  return { preferences, setPreference };
}
